import { DataQueueInterface } from '../queue.interface';
import { WindowServiceInterface } from '../../../service/window/service.interface';

export class DataQueueDelayed implements DataQueueInterface {
  /**
   * Delay in milliseconds before queue flush resolve promise
   */
  private delay: number = 2000;

  /**
   * Queue of data
   */
  private queue: { [key: string]: object[] } = {};

  /**
   * Timer handling the delay
   */
  private timer: { [key: string]: number } = {};

  /**
   *
   * @param windowService
   */
  constructor(private windowService: WindowServiceInterface) {}

  /**
   * @inheritDoc
   */
  public setDelay(delay: number): void {
    this.delay = delay;
  }

  /**
   * @inheritDoc
   */
  public getDelay(): number {
    return this.delay;
  }

  /**
   * @inheritDoc
   */
  public add(queueId: string, data: object): void {
    this.getQueue(queueId).push(data);
  }

  /**
   * @inheritDoc
   */
  public flush(queueId: string): Promise<object[]> {
    return new Promise((resolve, reject) => {
      // Has an active timer
      if (this.getTimer(queueId) > 0) {
        // Reject flush (so the promise does not stay in memory waiting for a resolve/reject call)
        reject();

        // Has no active timer
      } else {
        // Set new timer
        this.setTimer(
          queueId,
          this.windowService.setTimeout(() => {
            // Clone queue array
            const data = this.getQueue(queueId).slice(0);

            // Reset timer & queue
            this.resetTimer(queueId);
            this.resetQueue(queueId);

            // Resolve promise
            resolve(data);
          }, this.delay)
        );
      }
    });
  }

  /**
   * Resets the queue identified by @queueId
   *
   * @param queueId
   */
  public resetQueue(queueId: string): void {
    this.queue[queueId] = [];
  }

  /**
   * Gets the queue identified by @queueId, creates it if needed
   *
   * @param queueId
   */
  public getQueue(queueId: string): object[] {
    if (!this.queue[queueId]) {
      this.queue[queueId] = [];
    }

    return this.queue[queueId];
  }

  /**
   * Resets the queue's timer identified by @queueId
   *
   * @param queueId
   */
  public resetTimer(queueId: string): void {
    this.timer[queueId] = 0;
  }

  /**
   * Gets the queue's timer identified by @queueId
   *
   * @param queueId
   */
  public getTimer(queueId: string): number {
    return this.timer[queueId] || 0;
  }

  /**
   * Sets the queue's timer identified by @queueId
   *
   * @param queueId
   * @param timer
   */
  public setTimer(queueId: string, timer: number): void {
    this.timer[queueId] = timer;
  }
}
